import { useTranslation } from 'react-i18next'
import { StatsBar } from '../components/dashboard/StatsBar'
import { ActiveCallsGrid } from '../components/dashboard/ActiveCallsGrid'
import { CallsTable } from '../components/dashboard/CallsTable'
import { SpendSummaryCard } from '../components/dashboard/SpendSummaryCard'
import { TodaysCampaignCard } from '../components/dashboard/TodaysCampaignCard'
import { CallVolumeCard } from '../components/dashboard/CallVolumeCard'
import { Badge } from '../components/ui/Badge'
import { useTelemetryStore } from '../stores/telemetryStore'

// Voysi flamingo Dashboard. Layout mirrors screens-1.jsx::DashboardScreen:
//   Row 1: spend banner (1.4fr) + 2 KPI tiles
//   Row 2: 4 KPI tiles
//   Row 3: 1.65fr (Active calls accent + Recent calls table)
//         / 1fr  (Today's campaign + Call volume sparkline)
//
// Existing data hooks (useStats, useUsageSummary, useTelemetryStore,
// useCalls) are reused — only the visual layer changed.

export function DashboardPage() {
  const { t } = useTranslation('dashboard')
  const activeCallCount = useTelemetryStore((s) =>
    Object.values(s.activeCalls).filter((c) => c.status !== 'ended').length,
  )

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="font-display text-[20px] font-bold tracking-tight text-ink-900 md:text-[24px]">
          {t('title')}
        </h1>
        <p className="mt-1 text-[12.5px] text-ink-500">{t('subtitle')}</p>
      </div>

      {/* Row 1: spend banner + first two KPIs (the design's 1.4fr/1fr/1fr).
          On mobile we stack vertically; on lg the StatsBar handles its own
          internal 2x2 / 1x4 grid. We render SpendSummaryCard first, then
          StatsBar full-width below it for room-to-breathe — this is the
          biggest deviation from the design (which inlines the first 2
          KPIs alongside the banner), kept because the existing StatsBar
          renders all four metrics together. */}
      <SpendSummaryCard />

      {/* Row 2: 4 KPI cards */}
      <StatsBar />

      {/* Row 3: main 1.65fr / 1fr grid */}
      <div className="grid grid-cols-1 gap-[18px] xl:grid-cols-[1.65fr_1fr]">
        {/* Left column: Active calls accent card + Recent calls table */}
        <div className="flex min-w-0 flex-col gap-[18px]">
          <section
            className="overflow-hidden rounded-2xl border bg-ink-900 p-4"
            style={{ borderColor: '#FFD8E5' }}
          >
            <div className="mb-3 flex items-center justify-between px-1">
              <div className="font-display text-[14.5px] font-bold tracking-tight text-white">
                {t('sections.activeCalls')}
              </div>
              <Badge tone="success" pulse>
                {activeCallCount} live
              </Badge>
            </div>
            <ActiveCallsGrid />
          </section>

          <section
            className="overflow-hidden rounded-2xl border border-ink-200 bg-white p-4"
            style={{ boxShadow: 'var(--shadow-sm)' }}
          >
            <div className="mb-3 flex items-center justify-between px-1">
              <div className="font-display text-[14.5px] font-bold tracking-tight text-ink-900">
                {t('sections.callHistory')}
              </div>
            </div>
            <CallsTable />
          </section>
        </div>

        {/* Right column: Today's campaign + Call volume */}
        <div className="flex min-w-0 flex-col gap-[18px]">
          <TodaysCampaignCard />
          <CallVolumeCard />
        </div>
      </div>
    </div>
  )
}
